import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Bot } from "lucide-react";

const AI_MODES = [
  { value: "draft", label: "Draft only" },
  { value: "approval", label: "Approval required" },
  { value: "hybrid", label: "Hybrid" },
  { value: "automatic", label: "Fully automatic" },
  { value: "disabled", label: "Disabled" },
];

const DEFAULT_PROMPTS = {
  noc: "You are a NOC engineer. Ask for call examples (SIP call-id, ANI, DNIS, timestamp, source IP). Never claim an issue is fixed without confirmation.",
  billing: "You are a billing specialist. Never modify bank details or approve refunds above the configured limit. Ask for invoice number and account ID.",
  fraud: "You are a fraud analyst. Do NOT auto-reply to law enforcement, regulators, or traceback requests. Escalate to a human.",
  support: "You are a friendly customer support agent. Guide users politely; escalate technical to NOC and billing to Billing.",
  admin: "You handle general admin inquiries. Never grant privileges or expose credentials without human approval.",
};

export default function Agents() {
  const { activeCompanyId } = useAuth();
  const [depts, setDepts] = useState([]);
  const [selected, setSelected] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [threshold, setThreshold] = useState(0.75);
  const [mode, setMode] = useState("draft");

  const load = async () => {
    const params = activeCompanyId ? { company_id: activeCompanyId } : {};
    const { data } = await api.get("/departments", { params });
    setDepts(data);
    if (!selected && data.length) { pick(data[0]); }
  };

  const pick = (d) => {
    setSelected(d);
    setPrompt(d.ai_system_prompt || DEFAULT_PROMPTS[d.dept_type] || "");
    setThreshold(d.ai_confidence_threshold || 0.75);
    setMode(d.ai_mode || "draft");
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeCompanyId]);

  const save = async () => {
    if (!selected) return;
    try {
      await api.patch(`/departments/${selected.id}`, {
        ai_system_prompt: prompt,
        ai_confidence_threshold: threshold,
        ai_mode: mode,
      });
      toast.success("Agent updated");
      load();
    } catch (e) { toast.error("Failed"); }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden" data-testid="agents-page">
      <PageHeader title="Department AI agents" subtitle="Configure each department's agent — instructions, thresholds, and operating mode." />
      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[280px_1fr] overflow-hidden">
        <div className="border-r border-border overflow-y-auto">
          {depts.map((d) => (
            <button
              key={d.id}
              onClick={() => pick(d)}
              className={`w-full text-left p-3 border-b border-border email-row border-l-2 border-l-transparent ${selected?.id === d.id ? "active" : ""}`}
              data-testid={`agent-select-${d.id}`}
            >
              <div className="flex items-center gap-2">
                <Bot className="h-4 w-4 text-terracotta" />
                <div>
                  <div className="text-sm font-medium">{d.name}</div>
                  <div className="text-[11px] font-mono text-muted-foreground uppercase">{d.dept_type} · {d.ai_mode}</div>
                </div>
              </div>
            </button>
          ))}
          {depts.length === 0 && <div className="p-6 text-muted-foreground text-sm">No departments yet.</div>}
        </div>

        <div className="p-6 overflow-y-auto max-w-3xl">
          {selected ? (
            <div className="space-y-5">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-terracotta">Agent settings</div>
                <div className="font-display text-2xl tracking-tight mt-1">{selected.name}</div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Operating mode</Label>
                  <Select value={mode} onValueChange={setMode}>
                    <SelectTrigger className="rounded-sm" data-testid="agent-mode-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{AI_MODES.map((m) => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                    Auto-send confidence threshold: <span className="font-mono text-foreground">{Math.round(threshold * 100)}%</span>
                  </Label>
                  <Slider min={0.4} max={0.95} step={0.05} value={[threshold]} onValueChange={(v) => setThreshold(v[0])} data-testid="agent-threshold-slider" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Agent system prompt</Label>
                <Textarea rows={10} value={prompt} onChange={(e) => setPrompt(e.target.value)} className="rounded-sm font-mono text-sm" data-testid="agent-prompt-input" />
                <p className="text-xs text-muted-foreground">The AI will follow this guidance in every reply. Be specific about what it must never do.</p>
              </div>
              <Button onClick={save} className="rounded-sm" data-testid="agent-save-button">Save agent</Button>
            </div>
          ) : (
            <div className="text-muted-foreground text-sm">Select a department to configure its agent.</div>
          )}
        </div>
      </div>
    </div>
  );
}
